import type { GameKind, MatchSnapshot } from "@minigames/shared";

const BASE = "/api";

let token: string | null = null;

export function setToken(t: string | null): void {
  token = t;
}
export function getToken(): string | null {
  return token;
}

export class ApiError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly status: number,
  ) {
    super(message);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers: {
      "content-type": "application/json",
      ...(token ? { authorization: `Bearer ${token}` } : {}),
      ...init?.headers,
    },
  });

  const text = await res.text();
  const body = text ? (JSON.parse(text) as Record<string, unknown>) : {};

  if (!res.ok) {
    throw new ApiError(
      String(body["error"] ?? "unknown"),
      String(body["message"] ?? res.statusText),
      res.status,
    );
  }
  return body as T;
}

export interface AuthResult {
  token: string;
  expiresIn: number;
  user: {
    id: string;
    username: string | null;
    firstName: string | null;
    photoUrl: string | null;
    isPremium: boolean;
  };
  startParam: string | null;
}

export const api = {
  auth: (initData: string) =>
    request<AuthResult>("/auth", { method: "POST", body: JSON.stringify({ initData }) }),

  config: () =>
    request<{
      games: GameKind[];
      timeControls: Record<GameKind, string[]>;
      botUsername: string;
      miniAppShortName: string;
    }>("/config"),

  createMatch: (
    game: GameKind,
    timeControl: string,
    opponent: "friend" | "bot" = "friend",
    difficulty: "easy" | "normal" | "hard" = "normal",
  ) =>
    request<{ matchId: string; inviteLink: string | null; snapshot: MatchSnapshot }>("/matches", {
      method: "POST",
      body: JSON.stringify({ game, timeControl, opponent, difficulty }),
    }),

  rating: () =>
    request<{ season: unknown; ratings: unknown[] }>("/rating"),

  leaderboard: (game: string) =>
    request<{ season: unknown; leaders: unknown[] }>(`/rating/leaderboard/${game}`),

  adminStats: () =>
    request<{ totals: unknown; bySource: unknown[]; byGame: unknown[] }>("/admin/stats"),

  billing: () =>
    request<{
      plans: unknown[];
      perks: string[];
      entitlements: { premium: boolean; until: number | null; recurring: boolean; plan: string | null };
    }>("/billing"),

  invoice: (plan: string) =>
    request<{ link: string; plan: string; stars: number }>("/billing/invoice", {
      method: "POST",
      body: JSON.stringify({ plan }),
    }),

  history: () =>
    request<{
      matches: unknown[];
      stats: { total: number; wins: number; losses: number; draws: number };
    }>("/me/matches"),

  matchInfo: (id: string) =>
    request<{
      joinable: boolean;
      mine: boolean;
      game?: GameKind;
      timeControl?: string;
      status?: string;
      host?: { username: string | null; firstName: string | null; photoUrl: string | null } | null;
      snapshot?: MatchSnapshot;
    }>(`/matches/${id}`),
};
